import React, { FunctionComponent } from "react";
import { Button, IDataColumnDefProps, ITextQuery, PickerInput } from "@plex/react-components";
import { IListSchemasOptions, listSchemas } from "../NodeTypes/DataSchemas";
import { IViewController, useViewController } from "../ViewContext";
import { isFeatureToggleOn } from "../Util/NodeUtil";

interface ISchemaItem {
  id: string;
  name: string;
  system: string;
  dataType: string;
}

export interface ISchemaPickerProps {
  dialogTitle: string;
  initialSelection: { id: string; name: string } | undefined;
  options: IListSchemasOptions;
  showEditObjectsButton?: boolean;
  onChange?: (values: { id: string; name: string }[]) => void;
  "data-testid"?: string;
}

export const SchemaPicker: FunctionComponent<ISchemaPickerProps> = (props) => {
  const viewController: IViewController = useViewController();
  let [{ pickerSelected, pickerData, searchText }, setPicker] = React.useState({
    pickerSelected: props.initialSelection ? [props.initialSelection] : [],
    pickerData: [] as { id: string; name: string }[],
    searchText: ""
  });

  const handlePickerSearch = (query: ITextQuery, cols: Array<IDataColumnDefProps<ISchemaItem>>) => {
    const results: ISchemaItem[] = listSchemas(props.options);

    let items: {
      id: string;
      name: string;
    }[];
    if (cols.length === 0) {
      items = results.filter((x) => (x.name || "").toLowerCase().includes(query.query.toLowerCase()));
    } else {
      items = results.filter((row: any) =>
        query.isMatch(cols.map((col) => (col.valueSelector ? String(col.valueSelector(row)) : "")))
      );
    }

    setPicker((x: any) => ({ ...x, pickerData: items }));
  };

  const handlePickerSearchTextChanged = (value: string) => {
    setPicker((x: any) => ({ ...x, searchText: value }));
  };

  return (
    <div data-testid={props["data-testid"]}>
      <PickerInput<ISchemaItem>
        key={"schema_picker"}
        dialogTitle="Document Schemas"
        initialWidth={900}
        disabled={false}
        selected={pickerSelected}
        onSelectionChanged={(values: { id: string; name: string }[]) => {
          if (values.length === 0) {
            setPicker((x) => ({ ...x, pickerSelected: [], searchText: "" }));
            if (props.onChange) {
              props.onChange(values);
            }
            return;
          }

          pickerSelected = values.length > 0 ? [values[0]!] : [];
          setPicker((x) => ({
            ...x,
            pickerSelected: pickerSelected,
            searchText: ""
          }));

          if (props.onChange) {
            props.onChange(values);
          }
        }}
        keySelector={(row: ISchemaItem) => row?.id}
        displaySelector={(row: ISchemaItem) => row?.name}
        data={pickerData}
        onSearch={handlePickerSearch}
        searchDisabled={false}
        isOpen={false}
        multiSelect={false}
        maxDisplay={500}
        searchText={searchText}
        onSearchTextChange={handlePickerSearchTextChanged}
      >
        <PickerInput.Column<ISchemaItem>
          key="1"
          id={"displayName"}
          title="Name"
          valueSelector={(row: ISchemaItem) => row.id}
          searchable
          sortable
        >
          {(row: ISchemaItem) => <div data-testid={props["data-testid"] + "_" + row.name}>{row.name}</div>}
        </PickerInput.Column>
        <PickerInput.Column<ISchemaItem>
          key="2"
          id="system"
          title="System"
          sortable
          valueSelector={(row: ISchemaItem) => row.id}
        >
          {(row: ISchemaItem) => <div data-testid={props["data-testid"] + "_" + row.system}>{row.system}</div>}
        </PickerInput.Column>
        <PickerInput.Column<ISchemaItem>
          key="2"
          id="type"
          title="Type"
          sortable
          valueSelector={(row: ISchemaItem) => row.id}
        >
          {(row: ISchemaItem) => <div data-testid={props["data-testid"] + "_" + row.dataType}>{row.dataType}</div>}
        </PickerInput.Column>
      </PickerInput>
      {!isFeatureToggleOn("feat-core-3424-flows-user-defined-schema") ||
      props.options.referenceOnly ||
      !props.showEditObjectsButton ? (
        <></>
      ) : (
        <Button
          className="plex-fd-edit-objects-button"
          onClick={() =>
            viewController.setUserDefinedSchemasEditorState({
              ...viewController.userDefinedSchemasEditorState,
              show: true
            })
          }
        >
          Edit User Objects
        </Button>
      )}
    </div>
  );
};
